import { inject, Injectable } from '@angular/core';
import { UserPermissions } from '@grid-ui/api-models';
import { FeatureFlagService } from '@grid-ui/feature-flag';
import { PermissionSelector, PermissionsService } from '@grid-ui/permissions';
import { WhoAmIService } from '@grid-ui/whoami';
import { isNullOrUndefined } from '@maplecroft-typescript/utils';
import { firstValueFrom } from 'rxjs';
import { ServiceOffering } from '../../home/models';
import { AllServiceOfferings } from './service-offerings.const';

const DEFAULT_ORDER = 3;

@Injectable()
export class NavService {
  private readonly whoAmIService = inject(WhoAmIService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly featureFlagService = inject(FeatureFlagService);

  public async serviceOfferings(): Promise<ServiceOffering[]> {
    const internalOfferings = AllServiceOfferings.filter(
      ({ featureFlag }) => !featureFlag || this.featureFlagService.getFlagValue(featureFlag),
    )
      .filter(({ permissionSelector }) => !isNullOrUndefined(permissionSelector))
      .map((offering) =>
        this.permissionsService
          .isAllowed(offering.permissionSelector as PermissionSelector)
          .then((allowed) => ({ allowed, serviceOffering: offering })),
      );

    const externalOfferings = await firstValueFrom(this.whoAmIService.getPermissions()).then((permissions) =>
      this.getExternalServiceOfferings(permissions),
    );

    const allowedInternal = await Promise.all(internalOfferings).then((offerings) =>
      offerings.filter(({ allowed }) => allowed).map(({ serviceOffering }) => serviceOffering),
    );
    let orderedOfferings = [...allowedInternal, ...externalOfferings].sort((a, b) => (a.order > b.order ? 1 : -1));
    orderedOfferings = orderedOfferings.map((offering) => ({ ...offering, elementId: offering.key ? offering.key : offering.elementId }));

    const allMenusUpdated: Promise<void>[] = [];
    orderedOfferings.forEach((offering) => {
      if (offering.menu) {
        const menuItems = offering.menu
          .filter(({ featureFlagKey }) => this.featureFlagService.getFlagValue(featureFlagKey))
          .map(async (menuItem) =>
            menuItem.permissionSelector
              ? await this.permissionsService.isAllowed(menuItem.permissionSelector).then((allowed) => ({ allowed, menuItem }))
              : { allowed: true, menuItem },
          );
        allMenusUpdated.push(
          Promise.all(menuItems)
            .then((items) => items.filter(({ allowed }) => allowed).map(({ menuItem }) => menuItem))
            .then((items) => {
              offering.menu = items;
            }),
        );
      }
    });
    await Promise.all(allMenusUpdated);
    return orderedOfferings;
  }

  private getExternalServiceOfferings(permissions: UserPermissions): ServiceOffering[] {
    const externalTools = permissions['external-tools'];

    return externalTools.map((externalTool) => {
      const definedService = AllServiceOfferings.find(({ key }) => key === externalTool.key);
      const { name, image_url } = externalTool;

      if (definedService) {
        const { order, imageHref } = definedService;

        return {
          ...externalTool,
          order,
          imageHref,
          label: name,
        };
      }

      return {
        ...externalTool,
        order: DEFAULT_ORDER,
        imageHref: image_url.endsWith(`/media/public/icons/default.svg`) ? `shape-${externalTool.slug}` : image_url,
        label: name,
      };
    });
  }
}
